import React, { createContext, useContext, useEffect } from 'react';
import { Box, Divider } from '@material-ui/core';
import {
  INVENTORY_TYPES,
  QTY_INPUT_UI_MODE,
  STOCK_ADJUSTMENT_300_IN,
  STOCK_ADJUSTMENT_300_OUT,
  UNITS
} from 'src/constants';
import WarehouseCaption from './components/WarehouseCaption';
import Result from './components/Result';
import getColumns from './helpers/columnGenerator';
import { getSNCaption } from 'src/helpers';
import { useSelector } from 'react-redux';
import getTotals from './helpers/getTotals';
import solveExpression from './helpers/solveExpression';
import { qtyPCToCS } from 'src/helpers';

const {
  DISABLED,
  STOCK_TRANSFER_BY_WH,
  STOCK_ADJUSTMENT,
  STOCK_ADJUSTMENT_REPRICE
} = INVENTORY_TYPES;

const JVTransInvContext = createContext({});

function getWarehouseDescription({ warehouses, ixWH }) {
  return warehouses.find(item => item?.ixWH === ixWH)?.sWH ?? '';
}

function JVTransInv({ jv = {}, tableResult = () => {} }) {
  const {
    tax,
    priv,
    inv: { SN = {}, SN2 = {}, BN = {}, ExpDate = {}, ...rest },
    frontEnd
  } = useSelector(data => data.bizMeta);

  const labelsSetings = useSelector(state => state?.bizMeta);
  const labelParent = labelsSetings?.frontEnd?.labels?.products;
  const transLables = frontEnd?.labels ?? {};
  const prodLabels = frontEnd?.labels?.products ?? {};

  const INV_TYPE = jv?._rm?.ixInvType ?? DISABLED;
  const WAREHOUSES = jv?.trans_inv?.meta?.wh?.items ?? [];
  const ALLOW_VAT =
    jv?._rm?.webEntry?.trans_inv?.vat?.allow ?? tax?.vat?.allow ?? false;
  const isStockTransfer = INV_TYPE === STOCK_TRANSFER_BY_WH;
  const REPRICE = jv?._rm?.webEntry?.trans_inv?.reprice ?? 0;
  const REPRICE_CAPTION =
    jv?._rm?.webEntry?.trans_inv?.repriceCaption ?? 'Price';
  const MARK_UP = jv?._rm?.webEntry?.trans_inv?.mu ?? {
    allow: false,
    description: 'Markup'
  };
  const DISC_OUTRIGHT = jv?._rm?.webEntry?.trans_inv?.discOutright ?? {
    allow: false,
    description: 'Discount'
  };
  const DISC = jv?._rm?.webEntry?.trans_inv?.disc ?? {
    allow: false,
    description: 'Discount'
  };
  const DISC_BY_VALUE = jv?._rm?.webEntry?.trans_inv?.discValue ?? {
    allow: false,
    description: 'Discount'
  };

  const DISC_QTY = jv?._rm?.webEntry?.trans_inv?.discQty ?? {};

  const showCost = priv?.reports?.['gen.view-cost'] || false;

  const stkAdjViewType =
    jv?._rm?.webEntry?.trans_inv?.stock_adj_settings?.viewType ?? 'combined';

  // Hide Fields
  const hideFlds = jv?._rm?.webEntry?.trans_inv?.hideFlds ?? [];
  // insert fields
  const additionalFields = jv?._rm?.webEntry?.trans_inv?.additionalFields ?? [];

  const qtyInputUIMode =
    Boolean(rest?.CS?.allow) || Boolean(rest?.SC?.allow)
      ? jv?._rm?.webEntry?.trans_inv?.qty_input_ui_mode ??
        QTY_INPUT_UI_MODE.WITH_UNIT
      : QTY_INPUT_UI_MODE.BASIC;

  const viewType = jv?._rm?.webEntry?.trans_inv?.qty_view_type ?? 0;

  const stkAdjByVarUnits =
    jv?._rm?.webEntry?.trans_inv?.allow &&
    jv?._rm?.webEntry?.trans_inv?.qty_input_ui_mode === 3;

  const showInventoryPriceColumn = () => {
    if (INV_TYPE === STOCK_ADJUSTMENT_REPRICE) return false;
    return jv?._rm?.webEntry?.trans_inv?.showPriceColumn ?? true;
  };

  const generateColumns = (invType, ITEMS) => {
    const snPriceLog = jv?._rm?.sn_price_log?.allow ?? false;
    let cols = getColumns(snPriceLog ? 'SN_PRICE_LOG' : invType) ?? [];
    const showProdCode =
      jv?._rm?.webEntry?.trans_inv?.showProdCodeOnTransactions ??
      rest?.showProdCodeOnTransactions ??
      true;

    const productCodeIndex = cols.findIndex(item => item.fld === 'ProdCd');
    const productDescIndex = cols.findIndex(item => item.fld === 'sProd');

    let displayPrice;

    const transKey = jv?.JCd;
    const transValue = transLables?.trans?.[transKey];

    if (prodLabels) {
      if (transValue) {
        displayPrice = transValue;
      } else {
        displayPrice = prodLabels?.price;
      }
    } else {
      displayPrice = 'Price';
    }

    cols.forEach(col => {
      if (labelsSetings?.frontEnd?.labels) {
        if (col.fld === 'priceGross') {
          col.caption = displayPrice?.trim()
            ? `${displayPrice}`
            : col.caption ?? 'Inventory price';
        }
      }
    });

    if (cols[productCodeIndex]) {
      cols[productCodeIndex].caption = `${frontEnd?.labels?.products?.sProdCd ??
        'Product code'}`;
    }

    if (cols[productDescIndex]) {
      cols[productDescIndex].caption = `${frontEnd?.labels?.products?.sProd ??
        'Description'}`;
    }

    if (!showProdCode) {
      let index = cols.findIndex(col => col.fld === 'ProdCd');
      cols[index].show = false;
    }

    const hasSN = ITEMS.some(item => item.SN !== '');
    const snCaption = getSNCaption({
      settings: SN,
      products: ITEMS,
      type: 'SN'
    });

    if (hasSN) {
      let index = cols.findIndex(col => col.fld === 'SN');
      cols[index].show = true;
      cols[index].caption = snCaption;
    }

    const hasSN2 = ITEMS.some(item => item.SN2 !== '');
    const sn2Caption = getSNCaption({
      settings: SN2,
      products: ITEMS,
      type: 'SN2'
    });

    if (hasSN2) {
      let index = cols.findIndex(col => col.fld === 'SN2');
      cols[index].show = true;
      cols[index].caption = sn2Caption;
    }

    const hasBN = ITEMS.some(item => !!item.BN);
    if (hasBN) {
      let index = cols.findIndex(col => col.fld === 'BN');
      cols[index].show = true;
      cols[index].caption = BN?.label ?? 'BN';
    }

    const hasExpDate = ITEMS.some(item => !!item.ExpDate);
    if (hasExpDate) {
      let index = cols.findIndex(col => col.fld === 'ExpDate');
      cols[index].show = true;
      cols[index].caption = BN?.ExpDate_label ?? 'Exp Date';
    }

    const hasProdDate = ITEMS.some(item => !!item.ProdDate);
    if (hasProdDate) {
      let index = cols.findIndex(col => col.fld === 'ProdDate');
      cols[index].show = true;
      cols[index].caption = BN?.ProdDate_label ?? 'Prod. Date';
    }

    if (MARK_UP.allow) {
      const flds = ['muAmt', 'muSubTotal', 'netAmt'];
      flds.forEach(fld => {
        let index = cols.findIndex(col => col.fld === fld);
        if (index !== -1) {
          cols[index].show = true;
          cols[index].caption = cols[index].caption.replace(
            'Markup',
            MARK_UP.description
          );
        }
      });
    }

    if (DISC_OUTRIGHT.allow) {
      const flds = ['discOutrightAmt', 'discOutrightSubTotal', 'netAmt'];
      flds.forEach(fld => {
        let index = cols.findIndex(col => col.fld === fld);
        if (index !== -1) {
          cols[index].show = true;
          cols[index].caption = cols[index].caption.replace(
            'Outright Discount',
            DISC_OUTRIGHT?.description ?? 'Discount'
          );
        }
      });
    }
    // Discount by Percentage
    if (DISC.allow) {
      const flds = ['discAmt', 'netAmt'];
      flds.forEach(fld => {
        let index = cols.findIndex(col => col.fld === fld);
        if (index !== -1) {
          cols[index].show = true;
          cols[index].caption = cols[index].caption.replace(
            'Discount',
            DISC?.description ?? 'Discount'
          );
        }
      });
    }

    // Discount qty rate and amount
    if (DISC_QTY?.allow) {
      const flds = [
        'discQtyRate',
        'discQtyAmt',
        'discQtyNetAmt',
        'discQtySubTotal'
      ];

      flds.forEach(fld => {
        const index = cols.findIndex(col => col.fld === fld);

        if (index !== -1) {
          const config = DISC_QTY?.columns?.[fld];

          // Show only when configuration says show: true
          cols[index].show = config?.show === true;

          if (config?.description) {
            cols[index].caption = config.description;
          }
        }
      });
    }

    // Discount by Value
    if (DISC_BY_VALUE.allow) {
      const netAmtIndex = cols.findIndex(col => col.fld === 'netAmt');
      const remarksIndex = cols.findIndex(col => col.fld === 'vatCode');
      let position = '';

      //if (netAmtIndex !== -1) cols[netAmtIndex].caption = 'Sub-Totals(discValue)';

      if (remarksIndex !== -1) position = 'netAmt';
      else if (netAmtIndex !== -1) position = 'vatCode';

      additionalFields.push({
        align: 'right',
        caption: `${DISC_BY_VALUE?.description ?? 'Discount'} Amount`,
        fld: 'discValue',
        insertBefore: position,
        isCurrency: true,
        total: true,
        width: 120
      });

      if (netAmtIndex == -1) {
        additionalFields.push({
          align: 'right',
          caption: `Net Amount`,
          fld: 'discSubTotal',
          insertBefore: position,
          isCurrency: true,
          total: true,
          width: 120
        });
      }
    }

    // price gross
    if (MARK_UP.allow || DISC_OUTRIGHT.allow || DISC.allow) {
      let index = cols.findIndex(col => col.fld === 'price');
      if (index !== -1) cols[index].fld = 'priceGross';
    }

    // reprice
    if (REPRICE) {
      let newPriceindex = cols.findIndex(col => col.fld === 'new_price');
      let newVATINindex = cols.findIndex(col => col.fld === 'new_vatIN');

      if (newPriceindex !== -1) {
        cols[newPriceindex].show = true;
        cols[newPriceindex].caption = REPRICE_CAPTION;
      }

      if (newVATINindex !== -1) {
        cols[newVATINindex].show = true;
        cols[newVATINindex].caption = 'VAT';
      }
    }

    if (ALLOW_VAT) {
      const flds = ['vatINamt', 'vatOUTamt', 'vatCode'];
      flds.forEach(fld => {
        let index = cols.findIndex(col => col.fld === fld);
        if (index !== -1) cols[index].show = true;
      });
    }

    if (jv?._rm?.webEntry?.trans_inv?.priceCaption) {
      const priceIndex = cols.findIndex(col => col.fld === 'price');

      if (priceIndex !== -1) {
        cols[priceIndex].caption =
          jv?._rm?.webEntry?.trans_inv?.priceCaption ?? 'Price';
      }
    }

    if (!showInventoryPriceColumn()) {
      const flds = [
        'amt',
        'price',
        'priceIN',
        'priceOUT',
        'totalAmtIN',
        'totalAmtOUT',
        'vatINamt',
        'vatOUTamt',
        'vatCode'
      ];

      flds.forEach(fld => {
        let index = cols.findIndex(col => col.fld === fld);
        if (index !== -1) cols[index].show = false;
      });
    }

    if (qtyInputUIMode === QTY_INPUT_UI_MODE.CASE || viewType === 1) {
      additionalFields.push({
        align: 'right',
        caption: 'PC',
        fld: 'qty_PC',
        insertBefore: 'qty',
        isCurrency: true,
        total: true,
        width: 120
      });
      additionalFields.push({
        align: 'right',
        caption: 'SC',
        fld: 'qty_SC',
        insertBefore: 'qty_PC',
        isCurrency: true,
        total: true,
        width: 120
      });
      additionalFields.push({
        align: 'right',
        caption: 'CS',
        fld: 'qty_CS',
        insertBefore: 'qty_SC',
        isCurrency: true,
        total: true,
        width: 120
      });

      const qtyIndex = cols.findIndex(col => col.fld === 'qty');
      cols[qtyIndex].show = false;

      const unitIndex = cols.findIndex(col => col.fld === 'unit');
      cols[unitIndex].show = false;
    }

    if (hideFlds.length) cols = cols.filter(col => !hideFlds.includes(col.fld));

    if (additionalFields.length) {
      additionalFields.forEach(fld => {
        let newFld = { ...fld };

        // Prevent duplicate fields
        if (cols.find(item => item.fld === fld.fld)) return;

        // Find the index to insert before
        const index = cols.findIndex(col => col.fld === fld.insertBefore);

        // Only insert if the index is valid
        if (index === -1) {
          // Optionally, push to the end if insertBefore not found
          cols.push(newFld);
          return;
        }

        if (newFld?.formula) {
          newFld.renderValue = function(params) {
            return solveExpression({
              expression: newFld?.formula,
              variables: params
            });
          };
        }

        cols.splice(index, 0, newFld);
      });
    }

    if (snPriceLog) {
      cols = cols.filter(col => !['qtyIN', 'qtyOUT'].includes(col?.fld));
      // 1. Find index of the "SN" field
      const snIndex = cols.findIndex(col => col.fld === 'SN');

      // 2. Prepare the new column object
      const priceInCol = {
        fld: 'current_price',
        caption: 'Current Price',
        show: true,
        align: 'right',
        isCurrency: true,
        width: 120
      };

      // 3. Insert it at the "SN" index (shifts "SN" and everything after it to the right)
      if (snIndex !== -1) {
        cols.splice(snIndex + 1, 0, priceInCol);
      }
    }

    // Stock By Variance Show Units
    if (stkAdjByVarUnits) {
      const qtyREC_CS = cols.findIndex(col => col.fld === 'qtyREC_CS');
      cols[qtyREC_CS].show = true;
      const qtyREC_SC = cols.findIndex(col => col.fld === 'qtyREC_SC');
      cols[qtyREC_SC].show = true;
      const qtyREC_PC = cols.findIndex(col => col.fld === 'qtyREC_PC');
      cols[qtyREC_PC].show = true;

      const qtyACT_CS = cols.findIndex(col => col.fld === 'qtyACT_CS');
      cols[qtyACT_CS].show = true;
      const qtyACT_SC = cols.findIndex(col => col.fld === 'qtyACT_SC');
      cols[qtyACT_SC].show = true;
      const qtyACT_PC = cols.findIndex(col => col.fld === 'qtyACT_PC');
      cols[qtyACT_PC].show = true;

      const qtyIN_CS = cols.findIndex(col => col.fld === 'qtyIN_CS');
      cols[qtyIN_CS].show = true;
      const qtyIN_SC = cols.findIndex(col => col.fld === 'qtyIN_SC');
      cols[qtyIN_SC].show = true;
      const qtyIN_PC = cols.findIndex(col => col.fld === 'qtyIN_PC');
      cols[qtyIN_PC].show = true;

      const qtyOUT_CS = cols.findIndex(col => col.fld === 'qtyOUT_CS');
      cols[qtyOUT_CS].show = true;
      const qtyOUT_SC = cols.findIndex(col => col.fld === 'qtyOUT_SC');
      cols[qtyOUT_SC].show = true;
      const qtyOUT_PC = cols.findIndex(col => col.fld === 'qtyOUT_PC');
      cols[qtyOUT_PC].show = true;
    }

    // filter show
    cols = cols.filter(item => item?.show ?? true);
    return cols;
  };

  function getWH1Description() {
    const warehouse = getWarehouseDescription({
      warehouses: WAREHOUSES,
      ixWH: jv?.ixWH1 ?? 0
    });

    if (jv.ixWHSub || jv.ixWH1Sub) {
      if (isStockTransfer)
        return `${warehouse} - ${jv.trans_inv.wh.from.sWHSub}`;
      else return `${warehouse} - ${jv.trans_inv.wh.sWHSub}`;
    }

    return warehouse;
  }

  function getWH2Description() {
    const warehouse = getWarehouseDescription({
      warehouses: WAREHOUSES,
      ixWH: jv?.ixWH2 ?? 0
    });

    if (jv.ixWHSub || jv.ixWH1Sub) {
      if (isStockTransfer) return `${warehouse} - ${jv.trans_inv.wh.to.sWHSub}`;
      else return `${warehouse} - ${jv.trans_inv.wh.sWH2Sub}`;
    }

    return warehouse;
  }

  const getUnit = data => {
    if (qtyInputUIMode === QTY_INPUT_UI_MODE.WITH_UNIT && data.ixUnit === 1)
      return data.unitSC;
    else if (
      qtyInputUIMode === QTY_INPUT_UI_MODE.WITH_UNIT &&
      data.ixUnit === 2
    )
      return data.unitCS;
    else return data.unit;
  };

  const getQty = (data, qtyKey) => {
    if (!Boolean(data?.[qtyKey])) return 0;

    if (qtyInputUIMode === QTY_INPUT_UI_MODE.WITH_UNIT) {
      if (data.ixUnit === UNITS.SC)
        return data[qtyKey] / (data?.libQtySC ?? data.qtySC);
      else if (data.ixUnit === UNITS.CS)
        return data[qtyKey] / (data?.libQtyCS ?? data.qtyCS);
    }

    return data[qtyKey];
  };

  const getPrice = (data, priceKey) => {
    if (!Boolean(data?.[priceKey])) return 0;

    if (qtyInputUIMode === QTY_INPUT_UI_MODE.WITH_UNIT) {
      if (data.ixUnit === 1)
        return data[priceKey] * (data?.libQtySC ?? data.qtySC);
      else if (data.ixUnit === 2)
        return data[priceKey] * (data?.libQtyCS ?? data.qtyCS);
    }

    return data[priceKey];
  };

  const getItems = ({ key, invType }) => {
    const items = (jv?.trans_inv?.[key] ?? []).map(item => {
      let qtyREC = {};
      let qtyACT = {};
      let qtyIN = {};
      let qtyOUT = {};

      if (stkAdjByVarUnits) {
        qtyREC = qtyPCToCS({
          libQtyCS: item?.qtyCS || 0,
          libQtySC: item?.qtySC || 0,
          qty: item?.qtyREC || 0,
          keyProp: 'qty'
        });
        qtyACT = qtyPCToCS({
          libQtyCS: item?.qtyCS || 0,
          libQtySC: item?.qtySC || 0,
          qty: item?.qtyACT || 0,
          keyProp: 'qty'
        });
        qtyIN = qtyPCToCS({
          libQtyCS: item?.qtyCS || 0,
          libQtySC: item?.qtySC || 0,
          qty: item?.qtyIN || 0,
          keyProp: 'qty'
        });
        qtyOUT = qtyPCToCS({
          libQtyCS: item?.qtyCS || 0,
          libQtySC: item?.qtySC || 0,
          qty: item?.qtyOUT || 0,
          keyProp: 'qty'
        });
      }

      return {
        ...item,

        unit: getUnit(item),
        qty: getQty(item, 'qty'),
        priceGross: getPrice(item, 'priceGross'),
        price: getPrice(item, 'price'),
        qtyIN: getQty(item, 'qtyIN'),
        qtyOUT: getQty(item, 'qtyOUT'),
        priceIN: getPrice(item, 'priceIN'),
        priceOUT: getPrice(item, 'priceOUT'),
        totalAmtIN: (item?.qtyIN ?? 0) * (item?.priceIN ?? 0),
        totalAmtOUT: (item?.qtyOUT ?? 0) * (item?.priceOUT ?? 0),
        vatINamt:
          item.vatINamt ??
          (item.vatIN
            ? item.vatIN * (item.qty ?? 0)
            : ALLOW_VAT && item.vatCode === 1
            ? ((item.price ?? 0) * (item.qty ?? 0) * 0.12) / 1.12
            : 0),
        vatOUTamt:
          item.vatOUTamt || (item.vatOUT ? item.vatOUT * (item.qty || 0) : 0),
        vatCode: item.vatCode ?? 1,
        discOutrightAmt: item.discOutrightAmt,
        muAmt: item.muAmt,
        discAmt: item.discAmt,

        ...(stkAdjByVarUnits && {
          qtyREC_CS: qtyREC.qty_CS,
          qtyREC_SC: qtyREC.qty_SC,
          qtyREC_PC: qtyREC.qty_PC,
          qtyACT_CS: qtyACT.qty_CS,
          qtyACT_SC: qtyACT.qty_SC,
          qtyACT_PC: qtyACT.qty_PC,
          qtyIN_CS: qtyIN.qty_CS,
          qtyIN_SC: qtyIN.qty_SC,
          qtyIN_PC: qtyIN.qty_PC,
          qtyOUT_CS: qtyOUT.qty_CS,
          qtyOUT_SC: qtyOUT.qty_SC,
          qtyOUT_PC: qtyOUT.qty_PC
        })
      };
    });

    const columns = generateColumns(invType, items);

    return {
      items,
      columns,
      totals: getTotals(items, columns)
    };
  };

  const getResult = () => {
    let items = [],
      totals = {},
      columns = [],
      items_in = [],
      totals_in = {},
      items_out = [],
      totals_out = {},
      columns_in = [],
      columns_out = [];

    if (INV_TYPE === STOCK_ADJUSTMENT && stkAdjViewType === 'separated') {
      const itemsIN = getItems({
        key: 'items_in',
        invType: STOCK_ADJUSTMENT_300_IN
      });
      const itemsOUT = getItems({
        key: 'items_out',
        invType: STOCK_ADJUSTMENT_300_OUT
      });
      items_in = itemsIN.items;
      columns_in = itemsIN.columns;
      totals_in = itemsIN.totals;

      items_out = itemsOUT.items;
      columns_out = itemsOUT.columns;
      totals_out = itemsOUT.totals;
    } else if (INV_TYPE === STOCK_ADJUSTMENT && stkAdjViewType === 'combined') {
      const itemsIN = getItems({
        key: 'items_in',
        invType: INV_TYPE
      });
      const itemsOUT = getItems({
        key: 'items_out',
        invType: INV_TYPE
      });

      items = [...itemsIN.items, ...itemsOUT.items];
      columns = generateColumns(INV_TYPE, items);
      totals = getTotals(items, columns);
    } else {
      const res = getItems({
        key: 'items',
        invType: INV_TYPE
      });
      items = res.items;
      totals = res.totals;
      columns = res.columns;
    }

    let displayPrice;

    const transKey = jv?.JCd;
    const transValue = transLables?.trans?.[transKey];

    if (prodLabels) {
      if (transValue) {
        displayPrice = transValue;
      } else {
        displayPrice = prodLabels?.price;
      }
    } else {
      displayPrice = 'Price';
    }

    columns.forEach(col => {
      if (labelsSetings?.frontEnd?.labels) {
        if (col.fld === 'price') {
          col.caption = displayPrice?.trim()
            ? `${displayPrice}`
            : col.caption ?? 'Inventory price';
        }
      }
    });

    return {
      items,
      totals,
      columns,
      items_in,
      totals_in,
      items_out,
      totals_out,
      columns_in,
      columns_out
    };
  };

  const result = getResult();

  // Run only once when array is isEmpty this is connected to print layout
  useEffect(() => {
    const result = getResult();
    tableResult(result);
  }, []);

  return (
    <JVTransInvContext.Provider
      value={{
        jv,
        showCost,
        DISC,
        MARK_UP,
        DISC_OUTRIGHT,
        WAREHOUSES,
        INV_TYPE,
        ALLOW_VAT,
        SHOW_INV_PRICE_COLUMN: showInventoryPriceColumn()
      }}
    >
      <Box display="flex" gridGap="1em" ml={2} mb={2} mt={2}>
        <WarehouseCaption
          label={isStockTransfer ? 'From' : 'Warehouse'}
          description={getWH1Description()}
        />
        {isStockTransfer && (
          <WarehouseCaption label="To" description={getWH2Description()} />
        )}
      </Box>

      <Divider />
      {INV_TYPE === STOCK_ADJUSTMENT && stkAdjViewType === 'separated' ? (
        <Box display="flex" gridGap="0.5em" flexDirection="column">
          {/* Settings for Stock Bundle, use this default setting to get the correct output
            "stock_adj_settings": {
              "bottom": {
                "qtyType": "OUT",
                "title": "Bundled Items"
              },
              "top": {
                "limit": 1,
                "qtyType": "IN",
                "title": "Items"
              },
              "viewType": "separated"
            } */}
          <Result
            items={
              jv?._rm?.webEntry?.trans_inv?.stock_adj_settings?.top?.qtyType ??
              'IN'
                ? result.items_in
                : result.items_out
            }
            columns={
              jv?._rm?.webEntry?.trans_inv?.stock_adj_settings?.top?.qtyType ??
              'IN'
                ? result.columns_in
                : result.columns_in
            }
            totals={
              jv?._rm?.webEntry?.trans_inv?.stock_adj_settings?.top?.qtyType ??
              'IN'
                ? result.totals_in
                : result.columns_in
            }
            title={
              jv?._rm?.webEntry?.trans_inv?.stock_adj_settings?.top?.title ??
              'In'
            }
          />
          <Result
            items={
              jv?._rm?.webEntry?.trans_inv?.stock_adj_settings?.bottom
                ?.qtyType ?? 'OUT'
                ? result.items_out
                : result.items_in
            }
            columns={
              jv?._rm?.webEntry?.trans_inv?.stock_adj_settings?.top?.qtyType ??
              'OUT'
                ? result.columns_out
                : result.columns_out
            }
            totals={
              jv?._rm?.webEntry?.trans_inv?.stock_adj_settings?.top?.qtyType ??
              'OUT'
                ? result.totals_out
                : result.totals_out
            }
            title={
              jv?._rm?.webEntry?.trans_inv?.stock_adj_settings?.bottom?.title ??
              'OUT'
            }
          />
        </Box>
      ) : (
        <Result
          items={result.items}
          columns={result.columns}
          totals={result.totals}
        />
      )}
    </JVTransInvContext.Provider>
  );
}

export const useJVTransInv = () => useContext(JVTransInvContext);

export default JVTransInv;
